import { useState, useEffect } from "react";
import {
  Container,
  Title,
  Stack,
  Button,
  Card,
  Group,
  Text,
  Modal,
  TextInput,
  Textarea,
  ActionIcon,
  Badge,
  Alert,
  Divider,
} from "@mantine/core";
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { vendorCategoriesApi, VendorCategory, VendorSubcategory } from "../../../api/vendorCategories";

export function VendorCategoryManagement() {
  const queryClient = useQueryClient();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [categoryModalOpened, setCategoryModalOpened] = useState(false);
  const [subcategoryModalOpened, setSubcategoryModalOpened] = useState(false);
  const [deleteCategoryModalOpened, setDeleteCategoryModalOpened] = useState(false);
  const [deleteSubcategoryModalOpened, setDeleteSubcategoryModalOpened] = useState(false);
  const [editingCategory, setEditingCategory] = useState<VendorCategory | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<VendorSubcategory | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<VendorCategory | null>(null);
  const [deletingSubcategory, setDeletingSubcategory] = useState<VendorSubcategory | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  const { data: categories, isLoading } = useQuery({
    queryKey: ["vendor-categories"],
    queryFn: () => vendorCategoriesApi.listCategories(),
  });

  const createCategoryMutation = useMutation({
    mutationFn: vendorCategoriesApi.createCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor-categories"] });
      notifications.show({
        title: "Success",
        message: "Category created successfully",
        color: "green",
      });
      setCategoryModalOpened(false);
      setEditingCategory(null);
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to create category",
        color: "red",
      });
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      vendorCategoriesApi.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor-categories"] });
      notifications.show({
        title: "Success",
        message: "Category updated successfully",
        color: "green",
      });
      setCategoryModalOpened(false);
      setEditingCategory(null);
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to update category",
        color: "red",
      });
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: vendorCategoriesApi.deleteCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor-categories"] });
      notifications.show({
        title: "Success",
        message: "Category deleted successfully",
        color: "green",
      });
      setDeleteCategoryModalOpened(false);
      setDeletingCategory(null);
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to delete category",
        color: "red",
      });
    },
  });

  const createSubcategoryMutation = useMutation({
    mutationFn: ({ categoryId, data }: { categoryId: string; data: any }) =>
      vendorCategoriesApi.createSubcategory(categoryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor-categories"] });
      notifications.show({
        title: "Success",
        message: "Subcategory created successfully",
        color: "green",
      });
      setSubcategoryModalOpened(false);
      setEditingSubcategory(null);
      setSelectedCategoryId(null);
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to create subcategory",
        color: "red",
      });
    },
  });

  const updateSubcategoryMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      vendorCategoriesApi.updateSubcategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor-categories"] });
      notifications.show({
        title: "Success",
        message: "Subcategory updated successfully",
        color: "green",
      });
      setSubcategoryModalOpened(false);
      setEditingSubcategory(null);
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to update subcategory",
        color: "red",
      });
    },
  });

  const deleteSubcategoryMutation = useMutation({
    mutationFn: vendorCategoriesApi.deleteSubcategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor-categories"] });
      notifications.show({
        title: "Success",
        message: "Subcategory deleted successfully",
        color: "green",
      });
      setDeleteSubcategoryModalOpened(false);
      setDeletingSubcategory(null);
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to delete subcategory",
        color: "red",
      });
    },
  });

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setCategoryModalOpened(true);
  };

  const handleEditCategory = (category: VendorCategory) => {
    setEditingCategory(category);
    setCategoryModalOpened(true);
  };

  const handleDeleteCategory = (category: VendorCategory) => {
    setDeletingCategory(category);
    setDeleteCategoryModalOpened(true);
  };

  const handleCreateSubcategory = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setEditingSubcategory(null);
    setSubcategoryModalOpened(true);
  };

  const handleEditSubcategory = (subcategory: VendorSubcategory) => {
    setEditingSubcategory(subcategory);
    setSelectedCategoryId(subcategory.categoryId);
    setSubcategoryModalOpened(true);
  };

  const handleDeleteSubcategory = (subcategory: VendorSubcategory) => {
    setDeletingSubcategory(subcategory);
    setDeleteSubcategoryModalOpened(true);
  };

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading categories...</Text>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <Title order={2}>Vendor Category Management</Title>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreateCategory}
            color="green"
          >
            Add Category
          </Button>
        </Group>

        {!categories || categories.length === 0 ? (
          <Alert color="blue" title="No Categories">
            No vendor categories have been created yet. Click "Add Category" to create one.
          </Alert>
        ) : (
          <Stack gap="md">
            {categories.map((category) => (
              <Card key={category.id} shadow="sm" padding="lg" radius="md" withBorder>
                <Stack gap="md">
                  <Group justify="space-between">
                    <Group>
                      <ActionIcon
                        variant="subtle"
                        onClick={() => toggleCategory(category.id)}
                      >
                        {expandedCategories.has(category.id) ? (
                          <IconChevronUp size={20} />
                        ) : (
                          <IconChevronDown size={20} />
                        )}
                      </ActionIcon>
                      <div>
                        <Text fw={600} size="lg">
                          {category.name}
                        </Text>
                        {category.description && (
                          <Text size="sm" c="dimmed">
                            {category.description}
                          </Text>
                        )}
                        <Badge size="sm" color="blue" mt="xs">
                          {category.subcategories.length} subcategory
                          {category.subcategories.length !== 1 ? "ies" : ""}
                        </Badge>
                      </div>
                    </Group>
                    <Group>
                      <ActionIcon
                        color="blue"
                        variant="light"
                        onClick={() => handleEditCategory(category)}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon
                        color="red"
                        variant="light"
                        onClick={() => handleDeleteCategory(category)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                      <Button
                        size="xs"
                        leftSection={<IconPlus size={14} />}
                        onClick={() => handleCreateSubcategory(category.id)}
                        color="green"
                      >
                        Add Subcategory
                      </Button>
                    </Group>
                  </Group>

                  {expandedCategories.has(category.id) && (
                    <>
                      <Divider />
                      {category.subcategories.length === 0 ? (
                        <Text size="sm" c="dimmed" ta="center" py="md">
                          No subcategories yet. Click "Add Subcategory" to create one.
                        </Text>
                      ) : (
                        <Stack gap="sm">
                          {category.subcategories.map((subcategory) => (
                            <Group key={subcategory.id} justify="space-between" p="sm" style={{ border: "1px solid #e9ecef", borderRadius: "4px" }}>
                              <div>
                                <Text fw={500}>{subcategory.name}</Text>
                                {subcategory.description && (
                                  <Text size="sm" c="dimmed">
                                    {subcategory.description}
                                  </Text>
                                )}
                              </div>
                              <Group>
                                <ActionIcon
                                  color="blue"
                                  variant="light"
                                  onClick={() => handleEditSubcategory(subcategory)}
                                >
                                  <IconEdit size={16} />
                                </ActionIcon>
                                <ActionIcon
                                  color="red"
                                  variant="light"
                                  onClick={() => handleDeleteSubcategory(subcategory)}
                                >
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Group>
                            </Group>
                          ))}
                        </Stack>
                      )}
                    </>
                  )}
                </Stack>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>

      {/* Category Modal */}
      <CategoryModal
        opened={categoryModalOpened}
        onClose={() => {
          setCategoryModalOpened(false);
          setEditingCategory(null);
        }}
        category={editingCategory}
        onSubmit={(data) => {
          if (editingCategory) {
            updateCategoryMutation.mutate({ id: editingCategory.id, data });
          } else {
            createCategoryMutation.mutate(data);
          }
        }}
        loading={createCategoryMutation.isPending || updateCategoryMutation.isPending}
      />

      {/* Subcategory Modal */}
      <SubcategoryModal
        opened={subcategoryModalOpened}
        onClose={() => {
          setSubcategoryModalOpened(false);
          setEditingSubcategory(null);
          setSelectedCategoryId(null);
        }}
        subcategory={editingSubcategory}
        categoryId={selectedCategoryId || editingSubcategory?.categoryId || ""}
        onSubmit={(data) => {
          if (editingSubcategory) {
            updateSubcategoryMutation.mutate({ id: editingSubcategory.id, data });
          } else if (selectedCategoryId) {
            createSubcategoryMutation.mutate({ categoryId: selectedCategoryId, data });
          }
        }}
        loading={createSubcategoryMutation.isPending || updateSubcategoryMutation.isPending}
      />

      {/* Delete Category Confirmation */}
      <Modal
        opened={deleteCategoryModalOpened}
        onClose={() => {
          setDeleteCategoryModalOpened(false);
          setDeletingCategory(null);
        }}
        title="Delete Category"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete the category "{deletingCategory?.name}"? This will
            also delete all subcategories under this category and remove them from all vendors.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setDeleteCategoryModalOpened(false);
                setDeletingCategory(null);
              }}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => {
                if (deletingCategory) {
                  deleteCategoryMutation.mutate(deletingCategory.id);
                }
              }}
              loading={deleteCategoryMutation.isPending}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Subcategory Confirmation */}
      <Modal
        opened={deleteSubcategoryModalOpened}
        onClose={() => {
          setDeleteSubcategoryModalOpened(false);
          setDeletingSubcategory(null);
        }}
        title="Delete Subcategory"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete the subcategory "{deletingSubcategory?.name}"? This
            will remove it from all vendors who have selected it.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setDeleteSubcategoryModalOpened(false);
                setDeletingSubcategory(null);
              }}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => {
                if (deletingSubcategory) {
                  deleteSubcategoryMutation.mutate(deletingSubcategory.id);
                }
              }}
              loading={deleteSubcategoryMutation.isPending}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

// Category Modal Component
function CategoryModal({
  opened,
  onClose,
  category,
  onSubmit,
  loading,
}: {
  opened: boolean;
  onClose: () => void;
  category: VendorCategory | null;
  onSubmit: (data: { name: string; description?: string }) => void;
  loading: boolean;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (category) {
      setName(category.name);
      setDescription(category.description || "");
    } else {
      setName("");
      setDescription("");
    }
  }, [category, opened]);

  const handleSubmit = () => {
    if (!name.trim()) {
      notifications.show({
        title: "Error",
        message: "Category name is required",
        color: "red",
      });
      return;
    }
    onSubmit({ name: name.trim(), description: description.trim() || undefined });
    setName("");
    setDescription("");
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={category ? "Edit Category" : "Create Category"}
    >
      <Stack gap="md">
        <TextInput
          label="Category Name"
          placeholder="Enter category name"
          required
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <Textarea
          label="Description"
          placeholder="Enter category description (optional)"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          rows={3}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading} color="green">
            {category ? "Update" : "Create"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// Subcategory Modal Component
function SubcategoryModal({
  opened,
  onClose,
  subcategory,
  categoryId,
  onSubmit,
  loading,
}: {
  opened: boolean;
  onClose: () => void;
  subcategory: VendorSubcategory | null;
  categoryId: string;
  onSubmit: (data: { name: string; description?: string }) => void;
  loading: boolean;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (subcategory) {
      setName(subcategory.name);
      setDescription(subcategory.description || "");
    } else {
      setName("");
      setDescription("");
    }
  }, [subcategory, opened]);

  const handleSubmit = () => {
    if (!name.trim()) {
      notifications.show({
        title: "Error",
        message: "Subcategory name is required",
        color: "red",
      });
      return;
    }
    onSubmit({ name: name.trim(), description: description.trim() || undefined });
    setName("");
    setDescription("");
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={subcategory ? "Edit Subcategory" : "Create Subcategory"}
    >
      <Stack gap="md">
        <TextInput
          label="Subcategory Name"
          placeholder="Enter subcategory name"
          required
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <Textarea
          label="Description"
          placeholder="Enter subcategory description (optional)"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          rows={3}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading} color="green" disabled={!categoryId}>
            {subcategory ? "Update" : "Create"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

